// Editing the timeline the way Live's Arrangement View does it.
//
// Three rules from Live that the room's own builders don't follow:
//
// 1. CONTENT STAYS PUT. Trimming a clip's left edge, or splitting it, hides
//    or reveals audio; it never slides the audio along. A warped clip's start
//    marker moves with the edge, a looped one's wraps round its loop, and a
//    MIDI clip's notes shift so each still sounds where it did.
//
// 2. LANDING ON A CLIP OVERWRITES IT. A clip moved, pasted, duplicated,
//    dropped or stretched over another one trims it, splits it round the
//    newcomer, or deletes it when it's wholly covered. Nothing hunts for a
//    gap and nothing is refused for being in the way.
//
// 3. ONE GESTURE, ONE UNDO. The clearing and the placing travel as one
//    gesture, cleared first so the room sees no overlap at any step.
//
// What an object IS decides how much of rule 1 the wire can say
// (lib/clip-loop.ts): a WARPED clip's object is its source file and its
// markers are positions in it, so any span of it can be named. An UNWARPED
// timeline clip's object is a region render played from its top, which has
// no "start from the middle". So an unwarped clip that needs its head cut is
// rebased first: the same render, asserted warped at the room's tempo with
// its markers on its own span (extension `adoptSlotWireAudio` makes the same
// move for a slot render). At the room's tempo that stretches by exactly 1,
// so it sounds as it did, and from then on its markers mean what Live's do.

import type { ClipData } from "@shared/sync/protocol.js";
import type { Intent } from "@shared/sync/intents.js";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import { arrangementClipsOfTrack, type CanonicalClipState, type CanonicalRoom } from "@shared/sync/state.js";
import { canonicalNotes, MAX_NOTES_PER_CLIP, notesDigest, type WireNote } from "@shared/midi/notes.js";
import { clipDataOf, findClip, mintTempId } from "@web-shared/lib/edits";

/** Pieces shorter than this are not worth keeping: a clip that is all but
 * covered goes, rather than leaving a 1/1000-beat sliver behind. */
export const SLIVER_BEATS = 1 / 64;

const REPITCH = 3;
const COMPLEX = 4;

function mod(x: number, m: number): number {
  return x - m * Math.floor(x / m);
}

/**
 * The clip `d` cut (or grown) to the timeline span [from, to), its content
 * where it was. Null when the wire can't say it: an unwarped clip asked to
 * reveal audio before its render's top, or to move its head while it is
 * transposed (an unwarped transpose is a playback rate, which a rebase
 * would turn into a pitch shift); a warped clip asked to start before its
 * file; a MIDI clip over the note cap.
 */
export function cropData(d: ClipData, from: number, to: number, tempo: number): ClipData | null {
  const length = to - from;
  if (!(length > ASSERT_EPS) || from < -ASSERT_EPS) return null;
  const head = from - d.startTime;
  const tail = to - (d.startTime + d.duration);
  const placed = { startTime: Math.max(0, from), duration: length };

  if ((d.clipKind ?? "audio") === "midi") {
    // A flattened MIDI clip on the wire has no loop, but Live's MIDI clips
    // loop by default: growing one repeats what is in it, either way.
    const period = d.duration;
    const source = d.notes ?? [];
    const notes: WireNote[] = [];
    if (period > ASSERT_EPS) {
      const first = Math.floor(Math.min(0, head) / period);
      const last = Math.ceil((head + length) / period);
      for (let k = first; k < last; k++) {
        for (const n of source) {
          const at = n.startTime + k * period - head;
          if (!(at >= -ASSERT_EPS && at < length - ASSERT_EPS)) continue;
          // Cut where it stops sounding — the end of its repeat, the end of
          // the clip — as Live reads a clip back (flattenMidiClip). A note
          // left running past the end digests differently from the clip
          // every Live builds, and the lane never converges (2026-10-29,
          // the jam fuzzer: two end trims of one MIDI clip).
          const duration = Math.min(n.duration, Math.min((k + 1) * period - head, length) - at);
          if (duration > ASSERT_EPS) notes.push({ ...n, startTime: Math.max(0, at), duration });
        }
      }
    }
    if (notes.length > MAX_NOTES_PER_CLIP) return null;
    const canonical = canonicalNotes(notes);
    return {
      ...d,
      ...placed,
      startMarker: 0,
      endMarker: length,
      loopStart: 0,
      loopEnd: length,
      notes: canonical,
      notesDigest: notesDigest(canonical),
    };
  }

  if (!d.isWarped) {
    if (Math.abs(head) <= ASSERT_EPS) return { ...d, ...placed };
    if (head < 0 || (d.transpose ?? 0) !== 0 || (d.detune ?? 0) !== 0 || !(tempo > 0)) return null;
    // Rebased onto its render: warped at the room's tempo, markers on its span.
    const { warpMarkers: _dropped, ...rest } = d;
    void _dropped;
    return {
      ...rest,
      ...placed,
      isWarped: true,
      warpModeValue: d.warpModeValue === REPITCH ? COMPLEX : d.warpModeValue,
      sourceTempo: tempo,
      looping: false,
      startMarker: head,
      endMarker: head + length,
      loopStart: head,
      loopEnd: head + length,
    };
  }

  const loopLength = d.loopEnd - d.loopStart;
  if (d.looping && loopLength > ASSERT_EPS) {
    // Where the clip is `head` beats in, as a position in the file: past the
    // loop's end it has wrapped; before the start, inside the loop, it is
    // the pass before.
    let position = d.startMarker + head;
    const inLoop = d.startMarker >= d.loopStart - ASSERT_EPS;
    if (position >= d.loopEnd - ASSERT_EPS || (inLoop && position < d.loopStart - ASSERT_EPS)) {
      position = d.loopStart + mod(position - d.loopStart, loopLength);
    }
    if (position < -ASSERT_EPS) return null;
    return { ...d, ...placed, startMarker: Math.max(0, position) };
  }

  const startMarker = d.startMarker + head;
  if (startMarker < -ASSERT_EPS) return null;
  return {
    ...d,
    ...placed,
    startMarker: Math.max(0, startMarker),
    endMarker: Math.max(0, startMarker) + length,
    loopStart: d.loopStart + head,
    loopEnd: d.loopEnd + tail,
  };
}

/**
 * How far a clip's left edge can go before the wire can't follow: to 0 for
 * a MIDI clip or one looped from inside its loop, to the file's top for a
 * warped one, nowhere left of where it is for an unwarped one.
 */
export function earliestStart(d: ClipData): number {
  if ((d.clipKind ?? "audio") === "midi") return 0;
  if (!d.isWarped) return d.startTime;
  const loopLength = d.loopEnd - d.loopStart;
  if (d.looping && loopLength > ASSERT_EPS && d.startMarker >= d.loopStart - ASSERT_EPS) return 0;
  return Math.max(0, d.startTime - d.startMarker);
}

function update(clip: CanonicalClipState, data: ClipData, trackId = clip.trackId): Intent {
  return { kind: "upsertClip", id: clip.id, tempId: `edit_${clip.id}`, trackId, baseRev: clip.rev, clip: data, audioKey: null };
}

function create(clip: CanonicalClipState, data: ClipData, prefix: string): Intent | null {
  const midi = (clip.clipKind ?? "audio") === "midi";
  // A copy needs something to be a copy OF: the object, or the notes.
  if (!midi && clip.audioKey === null) return null;
  if (midi && !Array.isArray(clip.notes)) return null;
  return {
    kind: "upsertClip",
    id: null,
    tempId: mintTempId(prefix),
    trackId: clip.trackId,
    baseRev: null,
    clip: data,
    audioKey: midi ? null : clip.audioKey,
  };
}

/** Stamp a run of intents as one gesture (one undo, all or nothing). */
export function asGesture(intents: Intent[]): Intent[] {
  if (intents.length <= 1) return intents;
  const gestureId = mintTempId("gesture");
  return intents.map((i) => ({ ...i, gestureId }));
}

/**
 * Live's trim: one edge of a clip dragged to `beat`, the content staying
 * where it is. Clamped to what the clip can show (`earliestStart`) and to
 * `minDuration`. Null when nothing would change.
 */
export function trimClip(
  room: CanonicalRoom,
  clipId: string,
  edge: "start" | "end",
  beat: number,
  tempo: number,
  minDuration: number,
): Intent | null {
  const clip = findClip(room, clipId);
  if (!clip || clip.slot !== undefined) return null;
  const d = clipDataOf(clip);
  const end = d.startTime + d.duration;
  const from = edge === "start" ? Math.min(Math.max(beat, earliestStart(d)), end - minDuration) : d.startTime;
  const to = edge === "end" ? Math.max(beat, d.startTime + minDuration) : end;
  if (Math.abs(from - d.startTime) <= ASSERT_EPS && Math.abs(to - end) <= ASSERT_EPS) return null;
  const data = cropData(d, from, to, tempo);
  return data ? update(clip, data) : null;
}

/** A span of one lane, in timeline beats. */
export interface LaneSpan {
  trackId: string;
  from: number;
  to: number;
}

/**
 * What it takes to empty `spans` of every clip not in `ignore`: wholly
 * covered clips deleted, the rest trimmed to what's left — and a clip a
 * span lands inside split in two round it. Deletes first, then trims, then
 * the new right-hand pieces, so no step overlaps. Null when some clip in the
 * way can't be cut where it would have to be (`cropData`); the caller then
 * says so rather than half-doing it.
 */
export function clearSpans(
  room: CanonicalRoom,
  spans: readonly LaneSpan[],
  ignore: ReadonlySet<string>,
  tempo: number,
): Intent[] | null {
  const byTrack = new Map<string, Array<{ from: number; to: number }>>();
  for (const s of spans) {
    if (!(s.to - s.from > ASSERT_EPS)) continue;
    byTrack.set(s.trackId, [...(byTrack.get(s.trackId) ?? []), { from: s.from, to: s.to }]);
  }
  const deletes: Intent[] = [];
  const trims: Intent[] = [];
  const pieces: Intent[] = [];
  for (const [trackId, cuts] of byTrack) {
    for (const clip of arrangementClipsOfTrack(room, trackId)) {
      if (ignore.has(clip.id)) continue;
      const start = clip.startTime;
      const end = clip.startTime + clip.duration;
      // What survives: the clip's span minus every cut.
      let left: Array<{ from: number; to: number }> = [{ from: start, to: end }];
      for (const c of cuts) {
        left = left.flatMap((p) => {
          if (c.to <= p.from + ASSERT_EPS || c.from >= p.to - ASSERT_EPS) return [p];
          return [
            ...(c.from > p.from ? [{ from: p.from, to: c.from }] : []),
            ...(c.to < p.to ? [{ from: c.to, to: p.to }] : []),
          ];
        });
      }
      left = left.filter((p) => p.to - p.from >= SLIVER_BEATS);
      const untouched = left.length === 1 && Math.abs(left[0]!.from - start) <= ASSERT_EPS && Math.abs(left[0]!.to - end) <= ASSERT_EPS;
      if (untouched) continue;
      if (left.length === 0) {
        deletes.push({ kind: "deleteClip", id: clip.id, baseRev: clip.rev });
        continue;
      }
      const d = clipDataOf(clip);
      const [first, ...rest] = left;
      const kept = cropData(d, first!.from, first!.to, tempo);
      if (!kept) return null;
      trims.push(update(clip, kept));
      for (const p of rest) {
        const data = cropData(d, p.from, p.to, tempo);
        const made = data ? create(clip, data, "split") : null;
        if (!made) return null;
        pieces.push(made);
      }
    }
  }
  return [...deletes, ...trims, ...pieces];
}

/**
 * Land `placements` (creates, or updates moving a clip) the way Live does:
 * whatever is under them on their lanes is cleared first. The placed clips
 * themselves, and `ignore` (the rest of a moving selection), are never
 * cleared. Null when something in the way can't be cut.
 */
export function overwrite(
  room: CanonicalRoom,
  placements: readonly Intent[],
  tempo: number,
  ignore: Iterable<string> = [],
  /** Spans to empty as well as the placements' own: a pasted time selection
   *  replaces the whole of its span, the silence in it included. */
  extra: readonly LaneSpan[] = [],
): Intent[] | null {
  const spans: LaneSpan[] = [...extra];
  const skip = new Set(ignore);
  for (const p of placements) {
    if (p.kind !== "upsertClip") continue;
    if (p.id !== null) skip.add(p.id);
    if (p.clip.slot !== undefined) continue;
    spans.push({ trackId: p.trackId, from: p.clip.startTime, to: p.clip.startTime + p.clip.duration });
  }
  const clears = clearSpans(room, spans, skip, tempo);
  if (!clears) return null;
  return asGesture([...clears, ...placements]);
}

/**
 * Live's Split (⌘E): each clip that `atBeat` falls inside becomes two, cut
 * there, each half sounding as it did. Clips the cut misses, or would leave
 * a sliver of, are left alone.
 */
export function splitClipsAt(room: CanonicalRoom, clipIds: readonly string[], atBeat: number, tempo: number): Intent[] {
  return splitClipsAtPoints(room, clipIds, [atBeat], tempo);
}

/**
 * Split at several beats at once — Live's ⌘E on a time selection, which cuts
 * every clip it crosses at both of its edges, so the selected part becomes a
 * clip of its own. A clip the cuts miss (or would leave a sliver of) is left
 * alone; one that can't be cut where asked is left whole rather than half-cut.
 */
export function splitClipsAtPoints(
  room: CanonicalRoom,
  clipIds: readonly string[],
  points: readonly number[],
  tempo: number,
): Intent[] {
  const out: Intent[] = [];
  for (const id of clipIds) {
    const clip = findClip(room, id);
    if (!clip || clip.slot !== undefined) continue;
    const start = clip.startTime;
    const end = clip.startTime + clip.duration;
    const cuts = [...new Set(points)]
      .filter((p) => p - start >= SLIVER_BEATS && end - p >= SLIVER_BEATS)
      .sort((a, b) => a - b);
    if (cuts.length === 0) continue;
    const edges = [start, ...cuts, end];
    const d = clipDataOf(clip);
    const pieces: Intent[] = [];
    let ok = true;
    for (let i = 0; i < edges.length - 1 && ok; i++) {
      const data = cropData(d, edges[i]!, edges[i + 1]!, tempo);
      const made = data === null ? null : i === 0 ? update(clip, data) : create(clip, data, "split");
      if (made === null) ok = false;
      else pieces.push(made);
    }
    if (ok) out.push(...pieces);
  }
  return asGesture(out);
}

/** Why a split did nothing, for the one line under the timeline. */
export function splitMissNote(room: CanonicalRoom, clipIds: readonly string[], atBeat: number): string {
  const inside = clipIds
    .map((id) => findClip(room, id))
    .some((c) => c && c.slot === undefined && atBeat > c.startTime + SLIVER_BEATS && atBeat < c.startTime + c.duration - SLIVER_BEATS);
  return inside
    ? "That clip can't be split there (a transposed unwarped clip only splits once it's warped)."
    : "Put the insert marker inside a selected clip to split it there.";
}
